/**
 * Match marks — the four moments of a match, read off its video.
 *
 * One control for every page that sets them: Ops > Video times (the
 * Layers page's pop-out) and the calibration review's Game timing card.
 * Play, scan faster, or step to a moment, press "Mark" beside it; "Go"
 * jumps back to a mark. The page owns the field values and the save — the
 * two pages save to different routes — and hands in its <video> through
 * getVideo, read at click time so a player that mounts late still works.
 */
import { useEffect, useState } from 'react'

import { NO_FRAME_RATE_NOTE, servedFps } from '../lib/frameRate'

import './match-marks.css'

/** "1:17", "1:02:03" or "77" -> seconds; null when it is none of them. */
export function clockToSecs(v: string): number | null {
  const t = v.trim()
  if (!/^\d+(\.\d+)?$|^\d+:\d{1,2}(\.\d+)?$|^\d+:\d{1,2}:\d{1,2}(\.\d+)?$/.test(t)) return null
  return t.split(':').reduce((acc, part) => acc * 60 + parseFloat(part), 0)
}

/** Seconds -> "m:ss" / "h:mm:ss", to 0.1 s; '' when unset. */
export const secsToField = (s: number | null | undefined) => {
  if (s == null) return ''
  // Round first, so 59.96 s reads 1:00, not 0:60.
  const t = Math.round(s * 10) / 10
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60)
  const ss = Math.round((t % 60) * 10) / 10
  // By value, not string length: 5.5 s is '05.5', not '5.5'.
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`)
  return h ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`
}

/** The four marks, keyed as the finalize's game_timing is. */
export const MARKS = [
  { key: 'kickoff_s', label: 'Kick off' },
  { key: 'h1_end_s', label: 'End of first half' },
  { key: 'h2_start_s', label: 'Second half kick off' },
  { key: 'game_end_s', label: 'End of game' },
] as const
export type MarkKey = typeof MARKS[number]['key']
export type MarkFields = Record<MarkKey, string>
export const BLANK_MARKS: MarkFields =
  { kickoff_s: '', h1_end_s: '', h2_start_s: '', game_end_s: '' }

/** Marks out of order, as the server would refuse them; null when fine. */
export function orderProblem(values: (number | null)[]): string | null {
  const set = values.filter((v): v is number => v != null)
  return set.every((v, i) => i === 0 || (set[i - 1] ?? v) <= v)
    ? null : 'Times are out of order: kick off, first-half end, '
      + 'second-half kick off, end.'
}

/** What is wrong with the fields as typed, or null: a field that is not a
 *  time, then the order. */
export function marksProblem(fields: MarkFields): string | null {
  const values = MARKS.map((m) => clockToSecs(fields[m.key]))
  const bad = MARKS.find((m, i) => fields[m.key].trim() && values[i] == null)
  return bad ? `${bad.label}: not a time (use m:ss or seconds)`
    : orderProblem(values)
}

/** Playback speeds offered for scanning to a moment. */
export const SPEEDS = [0.5, 1, 2, 4, 8] as const

/** [size (seconds, or one frame), direction, label] */
const STEPS: [number | 'frame', 1 | -1, string][] = [
  [60, -1, '−1m'], [10, -1, '−10s'], [1, -1, '−1s'], ['frame', -1, '−1f'],
  ['frame', 1, '+1f'], [1, 1, '+1s'], [10, 1, '+10s'], [60, 1, '+1m'],
]

export default function MatchMarks({ getVideo, fps, fields, onChange, disabled }: {
  getVideo: () => HTMLVideoElement | null
  fps: number | null
  fields: MarkFields
  onChange: (key: MarkKey, value: string) => void
  /** Marks are shown but cannot be changed (and the video still plays). */
  disabled?: boolean
}) {
  const [now, setNow] = useState(0)
  const [rate, setRate] = useState(1)
  // Follow the video's clock without owning it: the element is the source
  // of truth, and it may mount after this does.
  useEffect(() => {
    const t = window.setInterval(() => {
      const v = getVideo()
      if (v) {
        setNow(v.currentTime)
        setRate(v.playbackRate)
      }
    }, 200)
    return () => window.clearInterval(t)
  }, [getVideo])

  const step = (secs: number | 'frame', sign: number) => {
    const v = getVideo()
    if (!v) return
    // a frame is one frame of THIS video's rate; with none recorded the
    // frame steps are disabled below rather than sized at a guess
    const videoFps = servedFps(fps)
    const d = secs !== 'frame' ? secs : videoFps === null ? null : 1 / videoFps
    if (d === null) return
    if (secs === 'frame') v.pause() // a frame step during playback is invisible
    v.currentTime = Math.max(0, v.currentTime + sign * d)
    setNow(v.currentTime)
  }
  const speed = (r: number) => {
    const v = getVideo()
    if (v) v.playbackRate = r
    setRate(r)
  }
  const values = MARKS.map((m) => clockToSecs(fields[m.key]))

  return (
    <div className="match-marks">
      <div className="adm-bar">
        <strong className="t-data" style={{ minWidth: 90 }}
                aria-label="Video time">{secsToField(now)}</strong>
        {STEPS.map(([secs, sign, label]) => (
          <button key={label} type="button" className="adm-btn adm-btn--quiet"
                  disabled={secs === 'frame' && servedFps(fps) === null}
                  title={secs === 'frame' && servedFps(fps) === null
                    ? NO_FRAME_RATE_NOTE : undefined}
                  onClick={() => step(secs, sign)}>{label}</button>
        ))}
      </div>
      <div className="adm-bar" role="group" aria-label="Playback speed">
        <span className="t-label">Speed</span>
        {SPEEDS.map((r) => (
          <button key={r} type="button" aria-pressed={rate === r}
                  className={`adm-btn${rate === r ? '' : ' adm-btn--quiet'}`}
                  onClick={() => speed(r)}>{r}×</button>
        ))}
      </div>
      <table className="adm-table" style={{ maxWidth: 640 }}>
        <tbody>
          {MARKS.map((m, i) => (
            <tr key={m.key}>
              <td>{m.label}</td>
              <td>
                <input className="adm-input" style={{ width: 110 }}
                       aria-label={m.label} placeholder="m:ss"
                       value={fields[m.key]} disabled={disabled}
                       onChange={(e) => onChange(m.key, e.target.value)} />
              </td>
              {/* The same time in raw seconds — the stack's tables and the
                  timing APIs all speak seconds, so the operator should not
                  need a calculator to cross-check them. */}
              <td className="muted t-data" style={{ minWidth: 70 }}
                  aria-label={`${m.label} in seconds`}>
                {values[i] != null
                  ? `${Math.round((values[i] as number) * 10) / 10} s` : ''}
              </td>
              <td>
                <button type="button" className="adm-btn" disabled={disabled}
                        onClick={() => onChange(m.key,
                          secsToField(getVideo()?.currentTime ?? now))}>
                  Mark {secsToField(now)}
                </button>{' '}
                <button type="button" className="adm-btn adm-btn--quiet"
                        disabled={values[i] == null}
                        onClick={() => {
                          const v = getVideo()
                          if (v && values[i] != null) {
                            v.currentTime = values[i]
                            setNow(values[i])
                          }
                        }}>
                  Go
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
